import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import { useTheme, alpha, darken } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StarIcon from '@mui/icons-material/Star';

import SidebarNavItem from './SidebarNavItem';
import SidebarNavGroup from './SidebarNavGroup';
import {
  useDesignSystemStore,
  isLocalStorageAvailable,
} from '../store/useDesignSystemStore';
import { getNavForRole } from '../../config/navConfig';
import { useAuth } from '../../context/AuthContext';
import usePendingFormsCount from '../../hooks/usePendingFormsCount';
import useOperationsHubCount from '../../hooks/useOperationsHubCount';
import useRequestBadgeCounts from '../../hooks/useRequestBadgeCounts';

/**
 * Sidebar — Professional dark sidebar navigation.
 * Clean hierarchy, tight spacing, polished feel.
 */
export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const sidebarCollapsed = useDesignSystemStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useDesignSystemStore((s) => s.toggleSidebar);
  const favorites = useDesignSystemStore((s) => s.favorites);
  const addFavorite = useDesignSystemStore((s) => s.addFavorite);
  const removeFavorite = useDesignSystemStore((s) => s.removeFavorite);
  const isFavorite = useDesignSystemStore((s) => s.isFavorite);
  const setMobileDrawerOpen = useDesignSystemStore((s) => s.setMobileDrawerOpen);

  const [searchQuery, setSearchQuery] = React.useState('');
  const [expandedGroupId, setExpandedGroupId] = React.useState(null);
  const expandedInitialized = useRef(false);
  const navRef = useRef(null);

  const lsAvailable = useMemo(() => isLocalStorageAvailable(), []);
  const navGroups = useMemo(() => getNavForRole(user), [user]);
  const pendingFormsCount = usePendingFormsCount();
  const opsHubCount = useOperationsHubCount();
  const requestCounts = useRequestBadgeCounts();

  // Badge map: nav item id → badge count
  const badgeCounts = useMemo(() => ({
    'my-asset-forms': pendingFormsCount,
    'operations-hub': opsHubCount,
    // Service Desk — unaddressed requests needing the current user's action
    'tickets': requestCounts.tickets,
    'ticket-pool': requestCounts.ticketPool,
    'server-requests': requestCounts.serverRequests,
    'vpn-requests': requestCounts.vpnRequests,
    'software-requests': requestCounts.softwareRequests,
    'hardware-my-requests': requestCounts.hardwareRequests,
    'service-renewals': requestCounts.reminders,
    'contract-analysis': requestCounts.contractAnalysis,
  }), [pendingFormsCount, opsHubCount, requestCounts]);

  useEffect(() => {
    if (expandedInitialized.current) return;
    const activeGroup = navGroups.find((group) =>
      !group.topLevel && group.children.some((item) =>
        item.exact
          ? location.pathname === item.path
          : location.pathname === item.path ||
            (item.path !== '/' && location.pathname.startsWith(item.path))
      )
    );
    if (activeGroup) {
      setExpandedGroupId(activeGroup.id);
      expandedInitialized.current = true;
    }
  }, [location.pathname, navGroups]);

  const allNavItems = useMemo(() => {
    const items = [];
    navGroups.forEach((group) => {
      group.children.forEach((item) => {
        items.push(item);
      });
    });
    return items;
  }, [navGroups]);

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const query = searchQuery.toLowerCase().trim();
    return allNavItems.filter((item) =>
      item.label.toLowerCase().includes(query)
    );
  }, [searchQuery, allNavItems]);

  const favoriteItems = useMemo(() => {
    if (!lsAvailable || favorites.length === 0) return [];
    return favorites
      .map((favId) => allNavItems.find((item) => item.id === favId))
      .filter(Boolean);
  }, [favorites, allNavItems, lsAvailable]);

  const handleNavigate = useCallback(
    (path) => {
      navigate(path);
      setMobileDrawerOpen(false);
      const targetGroup = navGroups.find((group) =>
        !group.topLevel && group.children.some((item) => item.path === path)
      );
      if (targetGroup) {
        setExpandedGroupId(targetGroup.id);
      }
    },
    [navigate, setMobileDrawerOpen, navGroups]
  );

  const handlePinToggle = useCallback(
    (itemId) => {
      if (isFavorite(itemId)) {
        removeFavorite(itemId);
      } else {
        addFavorite(itemId);
      }
    },
    [isFavorite, addFavorite, removeFavorite]
  );

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    if (value.length <= 100) {
      setSearchQuery(value);
    }
  }, []);

  const handleToggleGroup = useCallback((groupId) => {
    setExpandedGroupId((prev) => (prev === groupId ? null : groupId));
  }, []);

  const handleNavKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const container = navRef.current;
      if (!container) return;

      const focusable = Array.from(
        container.querySelectorAll('[role="menuitem"], [role="button"]')
      );
      const currentIndex = focusable.indexOf(document.activeElement);

      let nextIndex;
      if (e.key === 'ArrowDown') {
        nextIndex = currentIndex < focusable.length - 1 ? currentIndex + 1 : 0;
      } else {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : focusable.length - 1;
      }

      focusable[nextIndex]?.focus();
    }
  }, []);

  const collapsed = sidebarCollapsed;

  // ── Theme-driven sidebar surface ───────────────────────────────
  // Keeps the sidebar a readable dark navigation surface (its text/icons
  // use white-alpha), while tinting it with the active theme so it visibly
  // changes across presets and light/dark modes.
  const theme = useTheme();
  const sidebarBg = React.useMemo(() => {
    const primary = theme.palette.primary.main;
    if (theme.palette.mode === 'dark') {
      // In dark mode, follow the theme's own dark surface with a subtle primary tint.
      const base = theme.palette.background.default;
      return `linear-gradient(180deg, ${alpha(primary, 0.08)} 0%, transparent 35%), ${base}`;
    }
    // In light mode, build a deep, near-neutral slate with only a faint primary
    // tint — reads more premium/modern than a saturated navy block.
    const deep = darken(primary, 0.86);
    return `linear-gradient(180deg, ${alpha(primary, 0.10)} 0%, ${alpha(primary, 0.02)} 100%), ${deep}`;
  }, [theme]);

  const sidebarBorderColor = theme.palette.mode === 'dark'
    ? theme.palette.divider
    : alpha(theme.palette.primary.main, 0.14);

  return (
    <Box
      component="nav"
      aria-label="Main navigation"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: sidebarBg,
        borderRight: '1px solid',
        borderColor: sidebarBorderColor,
        overflow: 'hidden',
      }}
    >
      {/* ─── Collapse Toggle ──────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-end',
          px: collapsed ? 0 : 1.5,
          py: 0.75,
          flexShrink: 0,
        }}
      >
        <Tooltip
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          placement="right"
        >
          <IconButton
            onClick={toggleSidebar}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            size="small"
            sx={{
              width: 26,
              height: 26,
              color: 'rgba(255,255,255,0.45)',
              '&:hover': {
                color: 'rgba(255,255,255,0.8)',
                bgcolor: 'rgba(255,255,255,0.06)',
              },
            }}
          >
            {collapsed ? (
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            ) : (
              <ChevronLeftIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* ─── Search Input ─────────────────────────────────────────── */}
      {!collapsed && (
        <Box sx={{ px: 1.25, pb: 1, flexShrink: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'rgba(255,255,255,0.04)',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.08)',
              px: 1.25,
              py: 0.6,
              transition: 'all 150ms ease',
              '&:focus-within': {
                bgcolor: 'rgba(255,255,255,0.07)',
                borderColor: alpha(theme.palette.primary.light, 0.5),
              },
            }}
          >
            <SearchIcon
              sx={{ fontSize: 14, color: 'rgba(255,255,255,0.35)', mr: 0.75 }}
            />
            <InputBase
              placeholder="Search..."
              value={searchQuery}
              onChange={handleSearchChange}
              inputProps={{
                'aria-label': 'Search navigation',
                maxLength: 100,
              }}
              sx={{
                fontSize: '0.75rem',
                color: 'rgba(255,255,255,0.8)',
                flex: 1,
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(255,255,255,0.3)',
                  opacity: 1,
                },
              }}
            />
            {searchQuery && (
              <IconButton
                size="small"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                sx={{
                  p: 0.25,
                  ml: 0.25,
                  color: 'rgba(255,255,255,0.5)',
                  '&:hover': { color: 'rgba(255,255,255,0.9)', bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      {/* ─── Scrollable Nav Area ──────────────────────────────────── */}
      <Box
        ref={navRef}
        onKeyDown={handleNavKeyDown}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: collapsed ? 0.5 : 1.25,
          py: 0.5,
          '&::-webkit-scrollbar': { width: 3 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.1)',
            borderRadius: 2,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(255,255,255,0.2)',
          },
        }}
      >
        {/* Search Results */}
        {filteredItems !== null ? (
          filteredItems.length > 0 ? (
            <Box>
              {filteredItems.map((item) => (
                <SidebarNavItem
                  key={item.id}
                  item={item}
                  collapsed={collapsed}
                  onNavigate={handleNavigate}
                  onPin={lsAvailable ? handlePinToggle : undefined}
                  isPinned={isFavorite(item.id)}
                  showPinControl={lsAvailable}
                />
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>
                No results
              </Typography>
            </Box>
          )
        ) : (
          <>
            {/* Favorites Section */}
            {lsAvailable && favoriteItems.length > 0 && !collapsed && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.4,
                    mb: 0.25,
                  }}
                >
                  <StarIcon
                    sx={{ fontSize: 10, color: 'rgba(251,191,36,0.7)' }}
                  />
                  <Typography
                    sx={{
                      fontSize: '0.625rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'rgba(255,255,255,0.3)',
                    }}
                  >
                    Pinned
                  </Typography>
                </Box>
                {favoriteItems.map((item) => (
                  <SidebarNavItem
                    key={`fav-${item.id}`}
                    item={item}
                    collapsed={collapsed}
                    onNavigate={handleNavigate}
                    onPin={handlePinToggle}
                    isPinned={true}
                    showPinControl={true}
                  />
                ))}
                <Divider
                  sx={{ my: 1, borderColor: 'rgba(255,255,255,0.06)' }}
                />
              </>
            )}

            {/* Navigation Groups */}
            {navGroups.map((group) => (
              <SidebarNavGroup
                key={group.id}
                group={group}
                collapsed={collapsed}
                expandedGroupId={expandedGroupId}
                onToggleGroup={handleToggleGroup}
                onNavigate={handleNavigate}
                onPin={lsAvailable ? handlePinToggle : undefined}
                showPinControl={lsAvailable}
                badgeCounts={badgeCounts}
              />
            ))}
          </>
        )}
      </Box>

      {/* ─── User Footer ──────────────────────────────────────────── */}
      <Box
        sx={{
          flexShrink: 0,
          borderTop: '1px solid rgba(255,255,255,0.06)',
          px: collapsed ? 0.5 : 1.25,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: collapsed ? 0 : 1,
        }}
      >
        <Avatar
          sx={{
            width: 28,
            height: 28,
            fontSize: '0.7rem',
            fontWeight: 600,
            bgcolor: alpha(theme.palette.primary.main, 0.3),
            color: theme.palette.primary.light,
          }}
        >
          {user?.name
            ? user.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
            : 'U'}
        </Avatar>

        {!collapsed && (
          <Box sx={{ overflow: 'hidden', flex: 1 }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 500,
                color: 'rgba(255,255,255,0.8)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.3,
              }}
            >
              {user?.name || 'User'}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.625rem',
                color: 'rgba(255,255,255,0.35)',
                lineHeight: 1.3,
                textTransform: 'capitalize',
              }}
            >
              {user?.role?.replace(/_/g, ' ') || 'User'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
